import React, {
	Dispatch,
	memo,
	useCallback,
	useMemo,
	useState,
	useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Agent, Modal } from "../../../../services";
import { useTypedSelector } from "../../../../redux/store";
import { useTaxiServiceFilterAccessControl } from "../../../../hooks";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";
import { StyledP, StyledRow, StyledColumn } from "../..";

const EqualWidthColumn = styled(StyledColumn)`
	flex: 1;
`;

const MultiSelectAgentWithModal: React.FC<MultiSelectAgentWithModal.Props> = ({
	value = [],
	onChange,
	disabled,
	titleText,
	required,
	modalSetting,
	sort,
	subscribeOptions = {},
	taxiServiceIds,
	isOnCheckBox = true,
}) => {
	const { t } = useTranslation();
	const { accessTaxiServicesIds } = useTaxiServiceFilterAccessControl<any>();

	const lang = useTypedSelector((state) => state.session.language);

	const [error, setError] = useState("");
	const [items, setItems] = useState<
		Modal.ModelSimpleForMultySelectModalAgent[]
	>([]);
	const [loading, setLoading] = useState(false);

	const texts = useMemo(
		() => ({
			title: t("agentChoise"),
			allText: t("all_agent"),
			allActiveText: t("all_active_agent"),
			cardNumber: t("cardNumber"),
		}),
		[t],
	);

	const optionsService = useMemo<
		Omit<Modal.SubscribeOptions, "typedb">
	>(() => {
		const result: Omit<Modal.SubscribeOptions, "typedb"> = {
			lang,
			taxiServiceIds: taxiServiceIds || accessTaxiServicesIds,
			...subscribeOptions,
		};

		return result;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		accessTaxiServicesIds,
		lang,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		JSON.stringify(subscribeOptions),
		taxiServiceIds,
	]);

	useEffect(() => {
		if (isOnCheckBox === false) return () => {};

		let mounted = true;

		const loadData = async () => {
			setLoading(true);
			try {
				const data = await Modal.getAllAgent(optionsService);

				if (mounted) {
					setItems(data?.items ?? []);
				}
			} catch (err) {
				console.error("Failed to fetch Modal.getAll:", err);
				if (mounted) {
					setItems([]);
				}
			} finally {
				if (mounted) setLoading(false);
			}
		};

		loadData();

		return () => {
			mounted = false;
		};
	}, [isOnCheckBox, optionsService]);

	const selectOptions = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			items.map((item) => {
				const displayName =
					typeof item.name === "string"
						? item.name
						: item.name?.[lang] ?? "Unknown";
				const code = item.codeValue || "Unknown";
				const label = `${displayName} (${texts.cardNumber}: ${code})`;

				return {
					key: item.id,
					label,
					value: item.id,
					name: label,
					data: item,
				};
			}),
		[items, lang, texts.cardNumber],
	);

	const selected = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			selectOptions.filter((item) =>
				value.find((data) => data === item.value),
			),

		[selectOptions, value],
	);

	const selectOnChange = useCallback(
		(newValue) => {
			setError("");
			const ids = newValue.map((item) => item.value);
			onChange(ids);
		},
		[onChange],
	);

	useEffect(() => {
		if (disabled) {
			onChange([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [disabled]);

	return (
		<StyledRow gap="7px" alignItems="center">
			<EqualWidthColumn gap="7px">
				{titleText && <StyledP>{t(titleText) ?? ""}</StyledP>}
				<MultiSelectWithModal
					value={selected}
					onChange={selectOnChange}
					options={selectOptions}
					error={error}
					disabled={disabled}
					title={texts.title}
					allText={texts.allText}
					allActiveText={texts.allActiveText}
					required={required}
					pullUpItemInsideArray
					hiddenButton={false}
					showSelectAll
					modalSetting={modalSetting}
					sort={{
						show: true,
						active: true,
						...(sort || {}),
					}}
					loadingList={loading}
				/>
			</EqualWidthColumn>
		</StyledRow>
	);
};

declare namespace MultiSelectAgentWithModal {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText?: string | string[];
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		companyIds?: number[];
		subscribeOptions?: Agent.SubscribeOptions;
		canUseCompanyIds?: boolean;
		taxiServiceIds?: number[];
		isOnCheckBox?: boolean;
	}

	type Value = number[];
}

export default memo(MultiSelectAgentWithModal);
